import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Check } from "@/assets/icons/icons";
import {
  Button,
  ButtonAnchor,
  ButtonLink,
} from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import {
  Metric,
  MetricTiles,
} from "@/components/Layout/MetricTiles/MetricTiles";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";

export interface ProductCardListStep {
  label: string;
  status: "done" | "in-progress" | "to-be-done";
}

/**
 * What the footer button does: `to` navigates in the app and `href` opens an
 * external page in a new tab - both render as links. `onClick` is for actions.
 */
type FooterAction =
  | { to: string; href?: undefined; onClick?: undefined }
  | { href: string; to?: undefined; onClick?: undefined }
  | { onClick?: () => void; to?: undefined; href?: undefined };

type ProductCardFooterItemProps =
  | ({
      variant: "button";
      label: string;
      icon?: ReactNode;
      disabled?: boolean;
    } & FooterAction)
  | ({
      variant: "text-button";
      description: string;
      label: string;
      disabled?: boolean;
      loading?: boolean;
    } & FooterAction)
  | {
      variant: "qr-code";
      qrCode: ReactNode;
      description: string;
      link: ReactNode;
    }
  | { variant: "list"; title: string; steps: ProductCardListStep[] }
  | {
      variant: "progressbar";
      label: string;
      value: number;
      max: number;
      description: ReactNode;
    }
  | {
      variant: "notification";
      title: string;
      description?: string;
      tone?: "info" | "warning" | "error";
    }
  | { variant: "metric-cards"; cards: Metric[] };

const FooterButton = ({
  action,
  children,
  disabled,
  fullWidth,
  icon,
  loading,
}: {
  action: FooterAction;
  children: string;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  loading?: boolean;
}) => {
  if (action.to != null) {
    return (
      <ButtonLink
        variant="primary"
        to={action.to}
        disabled={disabled}
        fullWidth={fullWidth}
        icon={icon}
      >
        {children}
      </ButtonLink>
    );
  }

  if (action.href != null) {
    return (
      <ButtonAnchor
        variant="primary"
        href={action.href}
        disabled={disabled}
        fullWidth={fullWidth}
        icon={icon}
      >
        {children}
      </ButtonAnchor>
    );
  }

  return (
    <Button
      variant="primary"
      disabled={disabled}
      fullWidth={fullWidth}
      icon={icon}
      loading={loading}
      onClick={action.onClick}
    >
      {children}
    </Button>
  );
};

const stepIndicatorClassName: Record<ProductCardListStep["status"], string> = {
  done: "bg-surface-accent-blue text-foreground-primary-inverse",
  "in-progress": "bg-surface-accent-blue text-foreground-primary-inverse",
  "to-be-done": "bg-surface-secondary",
};

const ProductCardListStepRow = ({ label, status }: ProductCardListStep) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full items-center gap-2">
      <span
        className={clsx(
          "flex size-6 shrink-0 items-center justify-center rounded-full",
          stepIndicatorClassName[status],
        )}
      >
        {status === "done" && <Check className="size-4" />}
      </span>
      <p
        className={clsx(
          "truncate text-body-sm leading-md",
          status === "to-be-done"
            ? "text-foreground-tertiary"
            : "text-foreground-primary",
          status === "in-progress" && "font-semibold",
        )}
      >
        {label}
        {/* The indicator only shows the status by colour and icon. */}
        <span className="sr-only">
          {", "}
          {t(`translation:Common.stepStatus.${status}`)}
        </span>
      </p>
    </div>
  );
};

interface ProductCardFooterBoxProps {
  children: ReactNode;
  className?: string;
  dataTestid?: string;
}

/**
 * The quaternary-background box shared by every "info panel" style footer
 * item (list/progressbar here, plus every device tile that composes its own
 * bespoke footer content instead of a fixed variant above).
 */
export const ProductCardFooterBox = ({
  children,
  className,
  dataTestid,
}: ProductCardFooterBoxProps) => (
  <div
    data-testid={dataTestid}
    className={clsx(
      "flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4",
      // Tailwind resolves same-specificity utility conflicts by stylesheet
      // order, not by class-attribute order, so `items-start` and a
      // caller-supplied `items-*` override can't safely coexist in one
      // clsx() call -- omit the default whenever the caller supplies its own.
      // Only an unprefixed items-* token conflicts with the base items-start;
      // responsive/state variants like `tablet:items-center` apply on top of it
      // and must not suppress it.
      !className?.split(" ").some((token) => token.startsWith("items-")) &&
        "items-start",
      className,
    )}
  >
    {children}
  </div>
);

export const ProductCardFooterItem = (props: ProductCardFooterItemProps) => {
  switch (props.variant) {
    case "button":
      return (
        <FooterButton
          action={props}
          fullWidth
          icon={props.icon}
          disabled={props.disabled}
        >
          {props.label}
        </FooterButton>
      );

    case "text-button":
      return (
        <ProductCardFooterBox className="items-center text-center">
          <p className="text-body-base leading-md text-foreground-primary">
            {props.description}
          </p>
          <FooterButton
            action={props}
            disabled={props.disabled}
            loading={props.loading}
          >
            {props.label}
          </FooterButton>
        </ProductCardFooterBox>
      );

    case "qr-code":
      return (
        <div className="flex w-full items-start gap-4 rounded-md bg-surface-quaternary p-4">
          <div className="flex size-[88px] shrink-0 items-center justify-center rounded-md border border-border-tertiary p-2">
            {props.qrCode}
          </div>
          <div className="flex flex-1 flex-col items-start gap-2">
            <p className="text-body-base leading-md text-foreground-primary">
              {props.description}
            </p>
            {props.link}
          </div>
        </div>
      );

    case "list":
      return (
        <ProductCardFooterBox>
          <p className="w-full truncate text-headline-sm font-semibold leading-md text-foreground-primary">
            {props.title}
          </p>
          <ol className="flex w-full flex-col items-start gap-1">
            {props.steps.map((step, index) => (
              <li key={index} className="flex flex-col items-start gap-1">
                {index > 0 && (
                  <div className="flex w-6 shrink-0 justify-center" aria-hidden>
                    <div className="h-2 w-px bg-border-tertiary" />
                  </div>
                )}
                <ProductCardListStepRow {...step} />
              </li>
            ))}
          </ol>
        </ProductCardFooterBox>
      );

    case "progressbar": {
      const usageBarWidth =
        Number.isFinite(props.value) &&
        Number.isFinite(props.max) &&
        props.max > 0
          ? Math.min(100, Math.max(0, (props.value / props.max) * 100))
          : undefined;

      return (
        <ProductCardFooterBox>
          <p className="text-body-base font-semibold leading-none text-foreground-primary">
            {props.label}
          </p>
          <div className="flex w-full flex-col items-start gap-2">
            {usageBarWidth !== undefined && (
              <DireProgressBar value={usageBarWidth} />
            )}
            <p className="text-body-sm leading-none text-foreground-primary">
              {props.description}
            </p>
          </div>
        </ProductCardFooterBox>
      );
    }

    case "notification":
      return (
        <Notification
          title={props.title}
          description={props.description}
          tone={props.tone}
        />
      );

    case "metric-cards":
      return <MetricTiles metrics={props.cards} gap={4} />;

    default:
      return null;
  }
};
